import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Button,
    Box,
    Typography,
    LinearProgress,
    useTheme,
    alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Transition from '../common/Transition';

interface DeleteDialogProps {
    open: boolean;
    title: string;
    bodyText: string;
    isLoading: boolean;
    onClose: (reason?: string) => void;
    onSubmit: () => void;
    cancelButtonText: string;
    submitButtonText: string;
}

const DeleteDialog: React.FC<DeleteDialogProps> = ({
    open,
    title,
    bodyText,
    isLoading,
    onClose,
    onSubmit,
    cancelButtonText,
    submitButtonText,
}) => {
    const theme = useTheme();

    const handleClose = (_: React.SyntheticEvent, reason?: string) => {
        if (reason !== 'backdropClick') {
            onClose(reason);
        }
    };

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            aria-labelledby="delete-dialog-title"
            aria-describedby="delete-dialog-description"
        >
            <DialogTitle
                id="delete-dialog-title"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 3,
                    py: 2.5,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.04)})`,
                }}
            >
                <Typography 
                    variant="h5" 
                    component="span"
                    sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        letterSpacing: '-0.01em',
                    }}
                >
                    {title}
                </Typography>
                <IconButton 
                    onClick={() => onClose()} 
                    size="small"
                    sx={{
                        color: theme.palette.text.secondary,
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                            backgroundColor: alpha(theme.palette.error.main, 0.1),
                            color: theme.palette.error.main,
                            transform: 'scale(1.1)',
                        },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent 
                sx={{ 
                    px: 3,
                    py: 3,
                    minHeight: 80,
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <Typography 
                    variant="body1" 
                    id="delete-dialog-description"
                    sx={{
                        color: theme.palette.text.primary,
                        lineHeight: 1.6,
                        fontSize: '1rem',
                        fontWeight: 400,
                    }}
                >
                    {bodyText}
                </Typography>
            </DialogContent>

            {isLoading ? (
                <Box sx={{ px: 3, pb: 3 }}>
                    <LinearProgress 
                        sx={{ 
                            height: 3,
                            borderRadius: 1.5,
                            backgroundColor: alpha(theme.palette.error.main, 0.1),
                            '& .MuiLinearProgress-bar': {
                                background: `linear-gradient(90deg, ${theme.palette.error.main}, ${theme.palette.error.light})`,
                                borderRadius: 1.5,
                            },
                        }} 
                    />
                </Box>
            ) : (
                <DialogActions 
                    sx={{ 
                        px: 3, 
                        pb: 3,
                        pt: 1,
                        gap: 1.5,
                    }}
                >
                    <Button 
                        onClick={() => onClose()} 
                        variant="outlined"
                        size="medium"
                        sx={{
                            minWidth: 100,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 500,
                            borderColor: alpha(theme.palette.text.primary, 0.2),
                            color: theme.palette.text.primary,
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                borderColor: theme.palette.text.primary,
                                backgroundColor: alpha(theme.palette.text.primary, 0.04),
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.1)}`,
                            },
                        }}
                    >
                        {cancelButtonText}
                    </Button>
                    <Button 
                        onClick={onSubmit} 
                        variant="contained" 
                        color="error" 
                        size="medium"
                        sx={{
                            minWidth: 120,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                            boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`,
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                background: `linear-gradient(135deg, ${theme.palette.error.dark}, ${alpha(theme.palette.error.dark, 0.9)})`,
                                transform: 'translateY(-2px)',
                                boxShadow: `0 8px 20px ${alpha(theme.palette.error.main, 0.4)}`,
                            },
                            '&:active': {
                                transform: 'translateY(0px)',
                            },
                        }}
                    >
                        {submitButtonText}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    );
};

export default DeleteDialog;